/**
 * Customer Testimonials Carousel — Single Spotlight build
 * File: assets/customer-testimonials.js
 *
 * The center card is a fixed spotlight frame.
 * Prev/Next swaps the IMAGE inside it with a fade transition.
 * Left and right decorative cards show prev/next images statically.
 * Nothing translates or scrolls.
 */

(function () {
  'use strict';

  class TestimonialsCarousel {
    constructor(section) {
      this.section  = section;
      this.slides   = Array.from(section.querySelectorAll('.testimonials-slide'));
      this.dots     = Array.from(section.querySelectorAll('.testimonials-dot'));
      this.btnPrev  = section.querySelector('.testimonials-btn--prev');
      this.btnNext  = section.querySelector('.testimonials-btn--next');

      if (!this.slides.length) return;

      this.total      = this.slides.length;
      this.current    = Math.floor(this.total / 2);
      this.isAnimating = false;
      this.autoplayId = null;

      this.autoplay      = section.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(section.dataset.autoplaySpeed, 10) || 4) * 1000;

      this._drag = { active: false, startX: 0, currentX: 0, threshold: 50 };

      this._init();
    }

    _wrap(i) {
      return ((i % this.total) + this.total) % this.total;
    }

    _init() {
      // Hide all original slides — we build a fixed 5-slot layout instead
      this.slides.forEach(s => s.style.display = 'none');

      this._buildLayout();
      this._render(false);
      this._bindButtons();
      this._bindDots();
      this._bindKeys();
      this._bindTouch();
      this._bindMouse();

      if (this.autoplay) {
        this._startAutoplay();
        this.section.addEventListener('mouseenter', () => this._stopAutoplay());
        this.section.addEventListener('mouseleave', () => this._startAutoplay());
        this.section.addEventListener('focusin',    () => this._stopAutoplay());
        this.section.addEventListener('focusout',   () => this._startAutoplay());
      }
    }

    /**
     * Build a fixed 5-slot display inside the track container.
     * Slots: far-prev | prev | CENTER | next | far-next
     * The CENTER slot is the spotlight — its content swaps on nav.
     * Side slots show static preview cards.
     */
    _buildLayout() {
      const wrapper = this.section.querySelector('.testimonials-carousel-wrapper');
      if (!wrapper) return;

      // Remove old track container
      const oldContainer = this.section.querySelector('.testimonials-track-container');
      if (oldContainer) oldContainer.remove();

      // Build new fixed layout
      const layout = document.createElement('div');
      layout.className = 'tc-layout';
      layout.innerHTML = `
        <div class="tc-slot tc-slot--far-prev"  data-slot="far-prev"></div>
        <div class="tc-slot tc-slot--prev"       data-slot="prev"></div>
        <div class="tc-slot tc-slot--active"     data-slot="active"></div>
        <div class="tc-slot tc-slot--next"       data-slot="next"></div>
        <div class="tc-slot tc-slot--far-next"   data-slot="far-next"></div>
      `;

      // Insert between the two nav buttons
      const btnNext = this.section.querySelector('.testimonials-btn--next');
      wrapper.insertBefore(layout, btnNext);

      this.layout = layout;
    }

    /**
     * Clone the card at data-index into a slot.
     * We clone from the hidden original slides so each slot
     * always shows the correct card markup + image.
     */
    _cloneIntoSlot(slotEl, dataIndex) {
      const source = this.slides[dataIndex];
      if (!source) { slotEl.innerHTML = ''; return; }

      // Deep-clone the card inside the slide
      const card = source.querySelector('.testimonial-card');
      if (!card) { slotEl.innerHTML = ''; return; }

      slotEl.innerHTML = '';
      slotEl.appendChild(card.cloneNode(true));
    }

    /**
     * Render all 5 slots based on this.current.
     * animate: if false, skip fade (instant swap on init / resize).
     */
    _render(animate) {
      const slots = {
        'far-prev' : this._wrap(this.current - 2),
        'prev'     : this._wrap(this.current - 1),
        'active'   : this.current,
        'next'     : this._wrap(this.current + 1),
        'far-next' : this._wrap(this.current + 2),
      };

      const activeSlot = this.layout.querySelector('[data-slot="active"]');

      if (animate) {
        // Fade out center, swap content, fade back in
        activeSlot.classList.add('tc-fade-out');
        setTimeout(() => {
          this._cloneIntoSlot(activeSlot, slots['active']);
          activeSlot.classList.remove('tc-fade-out');
          activeSlot.classList.add('tc-fade-in');
          setTimeout(() => activeSlot.classList.remove('tc-fade-in'), 350);
        }, 200);
      } else {
        this._cloneIntoSlot(activeSlot, slots['active']);
      }

      // Side slots update instantly (no fade needed)
      ['far-prev', 'prev', 'next', 'far-next'].forEach(name => {
        const el = this.layout.querySelector(`[data-slot="${name}"]`);
        if (el) this._cloneIntoSlot(el, slots[name]);
      });

      this._updateDots();
    }

    _goTo(index, animate = true) {
      if (this.isAnimating && animate) return;
      this.isAnimating = true;

      this.current = this._wrap(index);
      this._render(animate);

      setTimeout(() => { this.isAnimating = false; }, 450);
    }

    _next() { this._goTo(this.current + 1); }
    _prev() { this._goTo(this.current - 1); }

    _updateDots() {
      this.dots.forEach((dot, i) => {
        const active = i === this.current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    _bindButtons() {
      if (this.btnPrev) this.btnPrev.addEventListener('click', () => this._prev());
      if (this.btnNext) this.btnNext.addEventListener('click', () => this._next());
    }

    _bindDots() {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this._goTo(i));
      });
    }

    _bindKeys() {
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._prev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._next(); }
        if (e.key === 'Home')       { e.preventDefault(); this._goTo(0); }
        if (e.key === 'End')        { e.preventDefault(); this._goTo(this.total - 1); }
      });
    }

    _bindTouch() {
      const el = this.section;
      el.addEventListener('touchstart', (e) => {
        this._drag.startX   = e.touches[0].clientX;
        this._drag.currentX = this._drag.startX;
        this._stopAutoplay();
      }, { passive: true });
      el.addEventListener('touchmove', (e) => {
        this._drag.currentX = e.touches[0].clientX;
      }, { passive: true });
      el.addEventListener('touchend', () => {
        const delta = this._drag.startX - this._drag.currentX;
        if (Math.abs(delta) > this._drag.threshold) {
          delta > 0 ? this._next() : this._prev();
        }
        if (this.autoplay) this._startAutoplay();
      });
    }

    _bindMouse() {
      const el = this.section;
      el.addEventListener('mousedown', (e) => {
        if (e.target.closest('.testimonials-btn') || e.target.closest('.testimonials-dots')) return;
        this._drag.active   = true;
        this._drag.startX   = e.clientX;
        this._drag.currentX = e.clientX;
        this._stopAutoplay();
      });
      window.addEventListener('mousemove', (e) => {
        if (!this._drag.active) return;
        this._drag.currentX = e.clientX;
      });
      window.addEventListener('mouseup', () => {
        if (!this._drag.active) return;
        this._drag.active = false;
        const delta = this._drag.startX - this._drag.currentX;
        if (Math.abs(delta) > this._drag.threshold) {
          delta > 0 ? this._next() : this._prev();
        }
        if (this.autoplay) this._startAutoplay();
      });
    }

    _startAutoplay() {
      this._stopAutoplay();
      this.autoplayId = setInterval(() => this._next(), this.autoplaySpeed);
    }
    _stopAutoplay() {
      if (this.autoplayId) { clearInterval(this.autoplayId); this.autoplayId = null; }
    }

    // Shopify compat
    _scrollToActive(animate) { this._render(animate); }
    _goToByBlock(index)      { this._goTo(index); }
  }

  function boot() {
    const sections = document.querySelectorAll('.testimonials-section');
    if (!sections.length) return;
    sections.forEach(s => { s.__carousel = new TestimonialsCarousel(s); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const s = e.target.querySelector('.testimonials-section');
      if (s) s.__carousel = new TestimonialsCarousel(s);
    });
    document.addEventListener('shopify:block:select', (e) => {
      const section = e.target.closest('.testimonials-section');
      if (!section) return;
      const slide = e.target.querySelector('.testimonials-slide');
      if (!slide) return;
      const index = parseInt(slide.dataset.index, 10);
      if (section.__carousel && !isNaN(index)) section.__carousel._goTo(index);
    });
  }

})();